import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Pencil,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
  Undo2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { highlightLockedPhrases } from '../../utils/reflectFlow';
import {
  PIZZUL_TIPS,
  claimTip,
  editableSheetPayload,
  groupRhymeFamilies,
  hookNeedsRhymeTip,
  lineEndingWord,
  missingSectionLabels,
  pocketMeter,
  sheetReducer,
  swapLastWord,
  totalLineCount,
} from '../../utils/reflectEditor';
import { vibeStyle } from '../../utils/vibeStyle';
import BoothSheet from './BoothSheet';
import LineField from './LineField';
import PizzulTip from './PizzulTip';
import RhymeBrain from './RhymeBrain';

const readSeenTips = (key) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (_error) {
    return [];
  }
};

const SongResults = ({
  cleanRhymes,
  draft,
  onNewDraft,
  onSaveSheet,
  onStory,
  questionByAnswerId,
  requestRhymes,
  sessionId,
}) => {
  const [sheet, dispatch] = useReducer(sheetReducer, draft);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [titleEditing, setTitleEditing] = useState(false);
  const [focusedLine, setFocusedLine] = useState(null);
  const [lookupTrigger, setLookupTrigger] = useState(null);
  const [deletedLine, setDeletedLine] = useState(null);
  const [tipQueue, setTipQueue] = useState([]);
  const lastSaved = useRef(JSON.stringify(editableSheetPayload(draft)));
  const sheetRef = useRef(sheet);
  const deletedTimer = useRef(null);
  const triggerId = useRef(0);
  const activeDraftId = useRef(draft.id);
  const tipStorageKey = `sp_reflect_tips_${sessionId}`;
  const seenTips = useRef(readSeenTips(tipStorageKey));
  const { color } = vibeStyle(sheet.vibe || '');

  sheetRef.current = sheet;

  useEffect(() => {
    if (activeDraftId.current === draft.id) return;
    activeDraftId.current = draft.id;
    dispatch({ type: 'reset', sheet: draft });
    lastSaved.current = JSON.stringify(editableSheetPayload(draft));
    setSaveStatus('Saved');
  }, [draft]);

  useEffect(() => {
    seenTips.current = readSeenTips(tipStorageKey);
    setTipQueue([]);
  }, [tipStorageKey]);

  useEffect(() => () => clearTimeout(deletedTimer.current), []);

  const showTip = useCallback((key, triggered = true) => {
    const claimed = claimTip(seenTips.current, key, triggered);
    if (!claimed.next) return;
    seenTips.current = claimed.seen;
    try {
      sessionStorage.setItem(tipStorageKey, JSON.stringify(claimed.seen));
    } catch (_error) {
      // Tips still work for this visit when session storage is unavailable.
    }
    setTipQueue((current) => [...current, claimed.next]);
  }, [tipStorageKey]);

  useEffect(() => {
    if (sheet.mode === 'just_write') showTip('just_write');
    showTip('editor_open');
  }, [sheet.mode, showTip]);

  useEffect(() => {
    const crowded = (sheet.sections || []).some((section) => (
      (section.lines || []).some((line) => pocketMeter(line.text).crowded)
    ));
    showTip('crowded', crowded);
  }, [sheet.sections, showTip]);

  useEffect(() => {
    showTip('hook_rhyme', hookNeedsRhymeTip(sheet.sections));
  }, [sheet.sections, showTip]);

  useEffect(() => {
    showTip('missing_lock', Boolean(sheet.missing_locks?.length));
  }, [sheet.missing_locks, showTip]);

  useEffect(() => {
    const payload = editableSheetPayload(sheet);
    const serialized = JSON.stringify(payload);
    if (serialized === lastSaved.current) return undefined;
    if (!sheet.title.trim()) return undefined;
    let active = true;
    setSaveStatus('Saving');
    const timer = window.setTimeout(async () => {
      try {
        const result = await onSaveSheet(payload);
        if (!active) return;
        lastSaved.current = JSON.stringify(editableSheetPayload(result));
        const current = JSON.stringify(editableSheetPayload(sheetRef.current));
        if (current === serialized) dispatch({ type: 'reset', sheet: result });
        setSaveStatus(current === serialized ? 'Saved' : 'Saving');
      } catch (error) {
        if (active) setSaveStatus(error.message);
      }
    }, 1000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [onSaveSheet, sheet]);

  const updateSheet = useCallback((action) => dispatch(action), []);

  const removeLine = (sectionIndex, lineIndex, line) => {
    dispatch({ type: 'remove_line', section: sectionIndex, line: lineIndex });
    setDeletedLine({ section: sectionIndex, line: lineIndex, value: line });
    clearTimeout(deletedTimer.current);
    deletedTimer.current = window.setTimeout(() => setDeletedLine(null), 6000);
  };

  const undoDelete = () => {
    if (!deletedLine) return;
    dispatch({ type: 'restore_line', ...deletedLine });
    setDeletedLine(null);
    clearTimeout(deletedTimer.current);
  };

  const lookupLine = (sectionIndex, lineIndex, text) => {
    const word = lineEndingWord(text);
    if (!word) return;
    setFocusedLine({ section: sectionIndex, line: lineIndex });
    triggerId.current += 1;
    setLookupTrigger({ id: triggerId.current, word });
  };

  const swapFocusedLine = (word) => {
    if (!focusedLine) return;
    const line = sheetRef.current.sections[focusedLine.section]?.lines[focusedLine.line];
    if (!line) return;
    dispatch({
      type: 'edit_line',
      section: focusedLine.section,
      line: focusedLine.line,
      text: swapLastWord(line.text, word),
    });
  };

  const addLine = (sectionIndex) => {
    dispatch({ type: 'add_line', section: sectionIndex });
    setFocusedLine({
      section: sectionIndex,
      line: sheet.sections[sectionIndex].lines.length,
    });
  };

  const activeTipKey = tipQueue[0];
  const activeTip = activeTipKey ? PIZZUL_TIPS[activeTipKey] : null;
  const canAddLines = totalLineCount(sheet.sections) < 48;

  return (
    <div className="mx-auto w-full max-w-7xl pb-20" style={{ '--glow': color }}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-extrabold uppercase tracking-[0.2em]" style={{ color }}>
          Your song
        </p>
        <p className={`text-base font-bold ${saveStatus === 'Saved' ? 'text-white/45' : 'text-white/70'}`}
          role="status" aria-live="polite">
          {saveStatus}
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <main className="min-w-0">
          <div className="mb-8 text-center">
            {titleEditing ? (
              <input
                value={sheet.title}
                onChange={(event) => dispatch({ type: 'edit_title', title: event.target.value })}
                onBlur={() => setTitleEditing(false)}
                maxLength={80}
                autoFocus
                className="min-h-14 w-full rounded-2xl border border-white/15 bg-black/25 px-4
                  text-center font-heading text-4xl font-black text-white outline-none
                  focus:border-[color:var(--glow)] sm:text-6xl"
                aria-label="Song title"
              />
            ) : (
              <button
                type="button"
                onClick={() => setTitleEditing(true)}
                className="group inline-flex min-h-14 max-w-full items-center gap-3 rounded-2xl px-3
                  font-heading text-4xl font-black focus:outline-none focus-visible:ring-2
                  focus-visible:ring-[color:var(--glow)] sm:text-6xl"
              >
                <span className="truncate">{sheet.title}</span>
                <Pencil className="h-5 w-5 shrink-0 text-white/35 group-hover:text-white" />
              </button>
            )}

            {sheet.mode !== 'just_write' && (
              <div className="mx-auto mt-6 max-w-lg">
                <div className="mb-2 flex items-end justify-between gap-4 text-left">
                  <p className="text-base font-extrabold">Your Voice</p>
                  <p className="font-heading text-2xl font-black" style={{ color }}>
                    {sheet.voice_percent || 0}%
                  </p>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-white/10"
                  aria-label={`Your Voice ${sheet.voice_percent || 0} percent`}>
                  <div className="h-full rounded-full" style={{
                    backgroundColor: color,
                    boxShadow: `0 0 16px ${color}88`,
                    width: `${Math.max(0, Math.min(100, sheet.voice_percent || 0))}%`,
                  }} />
                </div>
                <p className="mt-2 text-base text-white/55">of these words came from you</p>
              </div>
            )}
          </div>

          {sheet.missing_locks?.length > 0 && (
            <div className="mb-6 rounded-2xl border border-[#FF8A00]/40 bg-[#FF8A00]/10 p-5">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-[#FF8A00]" />
                <div>
                  <h2 className="font-heading text-xl font-black">A few locked words are missing</h2>
                  <p className="mt-1 text-base text-white/70">Put these back in by editing a line:</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {sheet.missing_locks.map((phrase) => (
                      <span key={phrase} className="rounded-full border border-[#FF8A00]/40 px-3
                        py-1.5 text-base font-bold">
                        {phrase}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-5">
            {(sheet.sections || []).map((section, sectionIndex) => {
              const rhymeFamilies = groupRhymeFamilies(section.lines);
              return (
                <section key={section.label} className="overflow-hidden rounded-[1.75rem] border
                  border-white/10 bg-white/[0.035] p-4 sm:p-6">
                  <h2 className="mb-4 font-heading text-xl font-black uppercase tracking-[0.12em]"
                    style={{ color }}>
                    {section.label}
                  </h2>
                  <div className="space-y-3">
                    {(section.lines || []).map((line, lineIndex) => {
                      const questions = (line.source_answer_ids || [])
                        .map((answerId) => questionByAnswerId[answerId])
                        .filter(Boolean);
                      const meter = pocketMeter(line.text);
                      const locked = highlightLockedPhrases(line.text, sheet.locked)
                        .some((part) => part.highlighted);
                      const family = rhymeFamilies[lineIndex];
                      return (
                        <div key={`${section.label}-${lineIndex}`} className="rounded-2xl border
                          border-white/10 bg-black/20 p-3">
                          <div className="flex items-center gap-2">
                            <LineField
                              value={line.text}
                              onChange={(event) => dispatch({
                                type: 'edit_line',
                                section: sectionIndex,
                                line: lineIndex,
                                text: event.target.value,
                              })}
                              onFocus={() => setFocusedLine({ section: sectionIndex, line: lineIndex })}
                              maxLength={140}
                              placeholder={section.label === 'Hook' ? 'Start your hook' : 'Start your line'}
                              className={`min-h-12 min-w-0 flex-1 resize-none overflow-hidden border-0
                                bg-transparent px-2 py-2.5 text-base font-bold leading-relaxed text-white outline-none
                                placeholder:text-white/30 sm:text-lg ${locked
                                  ? '[text-shadow:0_0_12px_var(--glow)]' : ''}`}
                              aria-label={`${section.label} line ${lineIndex + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => lookupLine(sectionIndex, lineIndex, line.text)}
                              disabled={!lineEndingWord(line.text)}
                              className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                                border border-white/10 text-white/60 hover:border-[color:var(--glow)]
                                hover:text-white disabled:opacity-30"
                              aria-label={`Find rhymes for ${lineEndingWord(line.text) || 'this line'}`}
                            >
                              <Search className="h-4 w-4" />
                            </button>
                            <span className="h-3 w-3 shrink-0 rounded-full" aria-label={family.key
                              ? `Rhyme family ${family.key}` : 'No rhyme family'} style={{
                              backgroundColor: family.key ? color : 'rgba(255,255,255,0.16)',
                              opacity: family.key ? Math.max(0.45, 1 - family.family * 0.12) : 1,
                              boxShadow: family.key ? `0 0 8px ${color}` : 'none',
                            }} />
                          </div>

                          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 pl-5">
                            <div>
                              <p className="text-base leading-relaxed text-white/50">
                                {questions.length
                                  ? `From your answer to: ${questions.join(' / ')}`
                                  : 'Written by you.'}
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className={`text-base font-black ${meter.crowded
                                  ? 'text-[#FFB347]' : 'text-white/55'}`}>
                                  {meter.count} syllables{meter.crowded ? ' · crowded' : ''}
                                </span>
                                <span className="flex gap-0.5" aria-label={`${meter.filled} of 16 notes filled`}>
                                  {Array.from({ length: 16 }).map((_, dot) => (
                                    <span key={dot} className="h-1.5 w-1.5 rounded-full" style={{
                                      backgroundColor: dot < meter.filled
                                        ? (meter.crowded ? '#FFB347' : color)
                                        : 'rgba(255,255,255,0.14)',
                                    }} />
                                  ))}
                                </span>
                              </div>
                            </div>

                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => dispatch({
                                  type: 'move_line',
                                  section: sectionIndex,
                                  line: lineIndex,
                                  direction: -1,
                                })}
                                disabled={lineIndex === 0}
                                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                                  text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-25"
                                aria-label="Move line up"
                              >
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => dispatch({
                                  type: 'move_line',
                                  section: sectionIndex,
                                  line: lineIndex,
                                  direction: 1,
                                })}
                                disabled={lineIndex === section.lines.length - 1}
                                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                                  text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-25"
                                aria-label="Move line down"
                              >
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeLine(sectionIndex, lineIndex, line)}
                                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                                  text-white/55 hover:bg-red-500/10 hover:text-red-200"
                                aria-label="Delete line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => addLine(sectionIndex)}
                    disabled={!canAddLines || section.lines.length >= 16}
                    className={`mt-3 min-h-12 rounded-full border border-dashed px-5 text-base
                      font-black hover:border-[color:var(--glow)] hover:text-white disabled:opacity-35
                      ${section.lines.length ? 'border-white/20 text-white/65'
                        : 'w-full border-[color:var(--glow)] text-white'}`}
                  >
                    + Add line{section.lines.length ? '' : ` · Start your ${section.label.toLowerCase()}`}
                  </button>
                </section>
              );
            })}
          </div>

          {missingSectionLabels(sheet.sections).length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {missingSectionLabels(sheet.sections).map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => dispatch({ type: 'add_section', label })}
                  className="min-h-12 rounded-full border border-white/15 px-5 text-base font-black
                    hover:border-[color:var(--glow)] hover:bg-white/[0.04]"
                >
                  + Add {label}
                </button>
              ))}
            </div>
          )}

          <BoothSheet
            cleanRhymes={cleanRhymes}
            draft={sheet}
            onChange={updateSheet}
            onFirstCopy={() => showTip('first_copy')}
          />

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <Button type="button" variant="outline" onClick={onNewDraft}
              className="min-h-12 rounded-full border-white/20 text-base font-black
                hover:border-[color:var(--glow)] hover:bg-white/[0.04]">
              <RotateCcw className="h-5 w-5" /> New draft
            </Button>
            <Button type="button" onClick={onStory}
              className="min-h-12 rounded-full text-base font-black text-black"
              style={{ backgroundColor: color }}>
              <Sparkles className="h-5 w-5" /> Make my Story Card
            </Button>
          </div>
        </main>

        <RhymeBrain
          color={color}
          focusedLine={focusedLine}
          lookupTrigger={lookupTrigger}
          onFirstLookup={() => showTip('first_lookup')}
          onSwap={swapFocusedLine}
          requestLookup={requestRhymes}
        />
      </div>

      {deletedLine && (
        <div className="fixed bottom-4 left-3 z-30 flex min-h-12 items-center gap-3 rounded-full
          border border-white/15 bg-[#111415] px-4 text-base font-bold shadow-2xl">
          Line deleted
          <button type="button" onClick={undoDelete}
            className="flex min-h-11 items-center gap-2 rounded-full px-3 font-black"
            style={{ color }}>
            <Undo2 className="h-4 w-4" /> Undo
          </button>
        </div>
      )}

      <PizzulTip
        tip={activeTip}
        onDismiss={() => setTipQueue((current) => current.slice(1))}
      />
    </div>
  );
};

export default SongResults;
